/**
 * Máy trạm SẢN XUẤT — bảng 36 máy vò treo ngoài xưởng, chỉ xem và nghe loa.
 *
 * Khác máy trạm đơn hàng (`/station`) ở ba điểm:
 *  - Bảng là danh sách MÁY, máy sắp dừng nằm trên cùng (người đứng xưởng chỉ
 *    quan tâm máy nào sắp phải ra chè).
 *  - Chỉ đọc loa cảnh báo máy sắp dừng; chuyện đơn hàng để máy trạm kho lo.
 *  - KHÔNG có nút điều khiển: tablet treo tường, chạm nhầm là dừng máy thật.
 *
 * Tên file có dấu `_` sau `station` để route nằm ở `/station/production` mà
 * không biến `station.tsx` thành layout (quy ước TanStack Router, giống
 * `$id_.edit.tsx` trong repo).
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { StationNotificationEvent } from '@/hooks/use-station-sse'
import type { MachineAnchor } from '@/lib/tea-machines/anchor'
import type { TeaMachine } from '@/lib/tea-machines/types'

import { StationShell, SummaryStat } from '@/components/station/station-shell'
import { StationMachineBoard } from '@/components/station/station-machine-board'
import { useStationAudio } from '@/hooks/use-station-audio'
import { useStationReadinessHeartbeat } from '@/hooks/use-station-readiness-heartbeat'
import { useStationSSE } from '@/hooks/use-station-sse'
import { useStaleSnapshotClock } from '@/hooks/use-stale-snapshot-clock'
import { useTeaMachines } from '@/hooks/use-tea-machines-query'
import { ApiError } from '@/lib/api'
import { resolveAnchorMs } from '@/lib/tea-machines/anchor'
import { sortMachinesForBoard } from '@/lib/tea-machines/board-sort'
import { m } from '@/lib/paraglide/messages'

/** Thời gian tô sáng một máy vừa được đọc loa. */
const HIGHLIGHT_MS = 15_000
export const Route = createFileRoute('/_app/_authed/station_/production')({
    component: StationProductionPage,
})

function StationProductionPage() {
    return <StationProductionView />
}

export function StationProductionView() {
    const navigate = useNavigate()
    const exitKiosk = () => navigate({ to: '/', search: { kioskExit: true } })
    const {
        soundEnabled,
        audioBlocked,
        enableSound,
        disableSound,
        playNotificationAlert,
        playTestSound,
    } = useStationAudio()

    const [highlighted, setHighlighted] = useState<Set<string>>(new Set())
    const highlightTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(
        new Map(),
    )
    useEffect(() => {
        const timers = highlightTimers.current
        return () => {
            for (const t of timers.values()) clearTimeout(t)
            timers.clear()
        }
    }, [])

    // Máy vừa được đọc loa: nháy sáng đủ lâu để người nghe ngẩng lên còn kịp
    // thấy — dài hơn bảng đơn vì người ở xưởng thường đang bận tay.
    const markAnnounced = useCallback((code: string | undefined) => {
        if (!code) return
        setHighlighted((prev) => new Set(prev).add(code))
        const running = highlightTimers.current.get(code)
        if (running) clearTimeout(running)
        const timer = setTimeout(() => {
            setHighlighted((prev) => {
                const next = new Set(prev)
                next.delete(code)
                return next
            })
            highlightTimers.current.delete(code)
        }, HIGHLIGHT_MS)
        highlightTimers.current.set(code, timer)
    }, [])

    const handleMachineFinishing = useCallback(
        (event: StationNotificationEvent) => {
            playNotificationAlert(event)
            // Server chỉ đính `speech_template` khi case đang bật trong cấu
            // hình lời thoại — không có nghĩa là không đọc, nên cũng không nháy.
            if (!event.speech_template) return
            markAnnounced(event.payload?.machine_code)
        },
        [playNotificationAlert, markAnnounced],
    )

    // Không truyền handler đơn hàng: màn này không đọc và cũng không hiện đơn.
    const { connected: sseConnected } = useStationSSE(
        undefined,
        undefined,
        handleMachineFinishing,
    )
    // Every signed-in account on this screen is a station; the server-side
    // checker decides which ones are monitored.
    const readiness = useStationReadinessHeartbeat(
        {
            sseConnected,
            soundEnabled,
            audioBlocked,
            kind: 'production',
        },
    )

    // Wake Lock — giữ màn hình tablet không ngủ.
    useEffect(() => {
        let wakeLock: WakeLockSentinel | null = null
        const request = async () => {
            try {
                wakeLock = await navigator.wakeLock.request('screen')
            } catch {
                /* not supported or denied */
            }
        }
        void request()
        const onVisible = () => {
            if (document.visibilityState === 'visible') void request()
        }
        document.addEventListener('visibilitychange', onVisible)
        return () => {
            void wakeLock?.release()
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [])

    const {
        data,
        dataUpdatedAt,
        error,
        errorUpdatedAt,
        isLoading,
        isError,
        refetch,
    } = useTeaMachines()
    const showStaleWarning = isError && data !== undefined
    const { nowMs, outageStartedAt } = useStaleSnapshotClock({
        isStale: showStaleWarning,
        errorUpdatedAt,
    })
    const requestId = error instanceof ApiError ? error.requestId : undefined
    // kind=config (SmartLab account rejected, integration off) slows
    // useTeaMachines to one poll every 5 minutes, so the kiosk recovers on its
    // own after an admin fixes SmartLab; the manual retry is for whoever is
    // standing in front of it and does not want to wait.
    const isConfigError = error instanceof ApiError && error.kind === 'config'

    // Anchor bền theo từng máy — chỉ đổi khi running_time/status thật sự đổi,
    // nhờ đó thanh tiến độ bò mượt thay vì giật về mỗi lần poll.
    const anchorsRef = useRef<Map<string, MachineAnchor>>(new Map())
    const machines: TeaMachine[] = useMemo(() => {
        const receivedAt = dataUpdatedAt || Date.now()
        const prev = anchorsRef.current
        const next = new Map<string, MachineAnchor>()
        const mapped = (data ?? []).map((dto) => {
            const anchorAtMs = resolveAnchorMs(
                prev.get(dto.machine_code),
                dto.status,
                dto.running_time,
                receivedAt,
            )
            next.set(dto.machine_code, {
                status: dto.status,
                runStartMin: dto.running_time,
                anchorAtMs,
            })
            return {
                code: dto.machine_code,
                status: dto.status,
                configuredMin: dto.configured_time,
                runStartMin: dto.running_time,
                anchorAtMs,
                updatedAtMs: Date.parse(dto.updated_at) || anchorAtMs,
            }
        })
        anchorsRef.current = next
        return mapped
    }, [data, dataUpdatedAt])

    // Sắp lại mỗi tick: máy sắp xong phải trôi dần lên đầu bảng.
    const ordered = useMemo(
        () => sortMachinesForBoard(machines, nowMs || Date.now()),
        [machines, nowMs],
    )
    const runningCount = useMemo(
        () => machines.filter((mc) => mc.status === 'running').length,
        [machines],
    )

    return (
        <StationShell
            title={m.station_production_title()}
            connected={sseConnected}
            liveLabel={m.station_live_machines()}
            soundEnabled={soundEnabled}
            audioBlocked={audioBlocked}
            onToggleSound={soundEnabled ? disableSound : enableSound}
            onTestSound={playTestSound}
            onExit={exitKiosk}
            readinessNotice={
                readiness.blocked && readiness.blockedAt
                    ? {
                          blockedAt: readiness.blockedAt,
                          requestId: readiness.requestId,
                      }
                    : undefined
            }
            footer={
                <footer className="station-chrome flex items-center justify-between gap-4 border-t border-[var(--line)] px-5 py-2.5 text-xs">
                    <div className="flex items-center gap-5">
                        <SummaryStat
                            tone="ready"
                            count={runningCount}
                            label={m.station_machine_summary_running()}
                        />
                        <SummaryStat
                            tone="amber"
                            count={machines.length - runningCount}
                            label={m.station_machine_summary_idle()}
                        />
                    </div>
                    <span className="font-semibold uppercase tracking-[0.18em] text-[var(--dim)]">
                        {m.station_machine_summary_total({
                            count: machines.length,
                        })}
                    </span>
                </footer>
            }
        >
            <main className="min-w-0 flex-1 overflow-auto">
                <StationMachineBoard
                    machines={ordered}
                    nowMs={nowMs || Date.now()}
                    highlighted={highlighted}
                    isLoading={isLoading}
                    isError={isError}
                    isConfigError={isConfigError}
                    onRetry={() => void refetch()}
                    staleAt={outageStartedAt}
                    requestId={requestId}
                />
            </main>
        </StationShell>
    )
}
